import React, { InputHTMLAttributes, forwardRef, useRef } from "react";
import { cva, VariantProps } from "class-variance-authority";
import { Icon, IconName } from "@/shared/ui/Icon";
import { IconButton } from "@/shared/ui/IconButton";
import { Row } from "@/shared/ui/Blocks";
import { cn } from "@/shared/utils/shadcn";

const inputFieldStyles = cva("w-full py-2 px-3 border rounded outline-none", {
  variants: {
    variant: {
      default: "border-gray-300",
      ghost: "border-none",
      white: "bg-white",
    },
    size: {
      small: "text-sm",
      md: "text-base",
      large: "text-lg",
    },
    active: {
      true: "ring-none",
    },
    disabled: {
      true: "opacity-50 cursor-not-allowed",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface FieldProps
  extends Omit<
      InputHTMLAttributes<HTMLInputElement>,
      "size" | "disabled" | "icon"
    >,
    Omit<VariantProps<typeof inputFieldStyles>, "icon"> {
  deleteIcon?: boolean;
  icon?: IconName;
  size?: "small" | "large";
  onClearValue?: () => void;
  wrapperClassName?: string;
  disabled?: boolean;
}

const InputField = forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      variant,
      size = "md",
      active,
      deleteIcon,
      icon,
      className,
      wrapperClassName,
      onClearValue,
      value,
      ...inputProps
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleCleanValue = () => {
      const input = ref && "current" in ref ? ref.current : inputRef.current;
      if (input) {
        input.value = "";
      }
      inputProps.onChange &&
        inputProps.onChange({ target: { value: "" } } as any);
      onClearValue && onClearValue();
    };

    return (
      <Row
        className={cn(
          "relative flex flex-1 items-center justify-between gap-2",
          value && value.toString().length > 0 && "writing",
          wrapperClassName
        )}
      >
        {icon && <Icon icon={icon} color={value ? "primary" : "grey"} />}
        <input
          ref={ref || inputRef}
          className={cn(
            inputFieldStyles({
              variant,
              size: size as any,
              active,
              disabled: inputProps.disabled,
            }),
            className
          )}
          {...inputProps}
        />
        {deleteIcon && value && value.toString().length > 0 && (
          <IconButton
            icon="Close"
            color="gray"
            onClick={handleCleanValue}
            className="absolute right-2"
            variant="ghost"
          />
        )}
      </Row>
    );
  }
);

export { InputField };
